import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  PageTabs,
  saveErrorReason,
  useBatchSelection,
  withErrorReason,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useLinesOfBusinessList, useLineOfBusinessMutations } from "../api";
import { useDownloadLinesOfBusinessCsv } from "../api/useDownloadLinesOfBusinessCsv";
import { buildLobBatchActions, type LobTab } from "./batchActions";
import type { LineOfBusiness } from "../types";

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<LobTab>("active");

  const sel = useBatchSelection([tab]);
  const { archive, restore, move } = useLineOfBusinessMutations();
  const { download, isDownloading } = useDownloadLinesOfBusinessCsv();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  // Lines of business are drag-reorderable by position, so load the full set
  // (no pagination — global position ordering and paging don't mix).
  const { rows, pageMeta, isLoading, isFetching, refetch } =
    useLinesOfBusinessList({
      page: { size: 500 },
      filter: { active: tab === "active" },
    });

  const selected = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<LineOfBusiness>[] = [
    {
      accessorKey: "name",
      header: "Line of Business",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "code",
      header: "Code",
      cell: ({ row }) => row.original.code,
    },
  ];

  const runOnSelected = async (
    fn: (l: LineOfBusiness) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selected];
    const failures: BatchFailure[] = [];
    for (const l of targets) {
      try {
        await fn(l);
      } catch (err) {
        failures.push({ label: l.name, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} line(s) of business.`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (l) => archive(l.id),
        (n) => `${n} line(s) of business archived.`
      );
    } else {
      await runOnSelected(
        (l) => restore(l.id, l.name),
        (n) => `${n} line(s) of business restored.`
      );
    }
  };

  const handleReorder = async (
    moves: Array<{ id: string | number; position: number }>
  ) => {
    try {
      for (const { id, position } of moves) {
        await move(String(id), position);
      }
      refetch();
    } catch (err) {
      setToast({
        variant: "danger",
        text: withErrorReason(
          "Could not reorder. The list has been refreshed.",
          err
        ),
      });
      refetch();
    }
  };

  const handleExport = async () => {
    try {
      await download(sel.selectedIds);
      setToast({ variant: "success", text: "Services CSV downloaded." });
      sel.clear();
    } catch {
      setToast({ variant: "danger", text: "Could not export services." });
    }
  };

  const batchActions = buildLobBatchActions(
    tab,
    sel.selectedIds.length,
    {
      onDelete: () => setConfirmOpen(true),
      onRestore: () => setConfirmOpen(true),
      onExport: handleExport,
    },
    isDownloading
  );

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Lines of Business"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Line of Business"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as LobTab);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          draggable={tab === "active"}
          onReorder={tab === "active" ? handleReorder : undefined}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No lines of business found."
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={
          tab === "active"
            ? "Archive lines of business?"
            : "Restore lines of business?"
        }
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} line(s) of business?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
